import { Helmet } from 'react-helmet-async';
import {
  canonicalUrl,
  isProductionHost,
  SITE_DESCRIPTION,
  SITE_IMAGE,
  SITE_NAME,
} from '@/shared/utils/site';

interface SeoProps {
  /** Page title; the site name is appended ("Daftar Buku | Rumah Kebijaksanaan") */
  title?: string;
  description?: string;
  /** Path of the page (`/store/book`); gives the canonical link and og:url */
  path?: string;
  /** Keeps the page out of search results (login-only and admin pages) */
  noindex?: boolean;
  /** Structured data (schema.org) for rich results, e.g. an ItemList of the books on sale */
  jsonLd?: Record<string, unknown>;
}

/**
 * Head tags of a page: title, description, canonical and Open Graph.
 *
 * A page nested inside another <Seo> (the default one in main.tsx, the
 * noindex one in RequireLogin) overrides only the tags it sets.
 */
const Seo = ({ title, description, path, noindex = false, jsonLd }: SeoProps) => {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : SITE_NAME;
  // Everything outside the production domain is hidden from search engines
  const hidden = noindex || !isProductionHost();
  const url = path && !hidden ? canonicalUrl(path) : undefined;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}
      {hidden && <meta name="robots" content="noindex, nofollow" />}
      {url && <link rel="canonical" href={url} />}
      {url && <meta property="og:url" content={url} />}
      <meta property="og:title" content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:image" content={SITE_IMAGE} />
      {jsonLd && !hidden && <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>}
    </Helmet>
  );
};

/** The tags every page starts from; a page's own <Seo> replaces what it sets. */
export const DefaultSeo = () => <Seo description={SITE_DESCRIPTION} />;

export default Seo;
